import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/use-auth";
import { supabase } from "../auth/supabase-client";
import {
  clearRecoveryLocation,
  parseRecoveryIntent,
  type RecoveryIntent,
} from "../auth/recovery-link";
import bdseLogoUrl from "../assets/bdse-logo.jpg";

/**
 * Password reset flow. Two purposes served by this page based on URL state:
 *
 *  1. /forgot-password: User enters their email, we call
 *     supabase.auth.resetPasswordForEmail which sends a recovery link.
 *     The link redirects back to /reset-password (this same page) with a
 *     Supabase recovery token in the URL hash.
 *
 *  2. /reset-password: A scanner-resistant BDSE link carries a token hash in
 *     the URL fragment. Nothing consumes that one-time credential until the
 *     human explicitly continues. Legacy implicit-flow links remain supported
 *     when Supabase has already established a recovery session.
 *
 * We detect which mode we're in by the `mode` prop, set by AppRouter based
 * on the route path. Splitting this into two separate components would be
 * cleaner but adds another file and route; for v1 a single-component
 * conditional is acceptable.
 */
export function ResetPassword({ mode }: { mode: "request" | "confirm" }) {
  const navigate = useNavigate();
  const { resetPasswordForEmail } = useAuth();
  const [recoveryIntent] = useState<RecoveryIntent>(() =>
    mode === "confirm"
      ? parseRecoveryIntent(window.location.search, window.location.hash)
      : { kind: "none" }
  );
  const [confirmState, setConfirmState] = useState<
    "checking" | "awaiting_confirmation" | "ready" | "invalid"
  >(mode === "confirm" ? "checking" : "ready");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== "confirm") return;
    let active = true;

    if (recoveryIntent.kind === "token") {
      // Keep the credential only in component memory while the user decides
      // whether to continue. It must not remain in history, logs, or referrers.
      clearRecoveryLocation();
      setConfirmState("awaiting_confirmation");
      return;
    }
    if (recoveryIntent.kind === "invalid") {
      clearRecoveryLocation();
      setConfirmState("invalid");
      setError("This password link is invalid or has expired. Request a new link to continue.");
      return;
    }

    void supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!active) return;
      if (sessionError || !data.session) {
        setConfirmState("invalid");
        setError("This password link is invalid or has expired. Request a new link to continue.");
        return;
      }
      setConfirmState("ready");
    });

    return () => {
      active = false;
    };
  }, [mode, recoveryIntent]);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    const result = await resetPasswordForEmail(email);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setSuccess(
      "If an account exists for this email, a password reset link has been sent. Check your inbox."
    );
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session) {
      setBusy(false);
      setConfirmState("invalid");
      setError("Your secure password session has expired. Request a new link to continue.");
      return;
    }
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });
    setBusy(false);
    if (updateError) {
      if (updateError.message.toLowerCase().includes("session")) {
        setConfirmState("invalid");
        setError("Your secure password session has expired. Request a new link to continue.");
      } else {
        setError("Your password could not be updated. Please try again.");
      }
      return;
    }
    setSuccess("Password updated successfully. You are now signed in.");
    if (recoveryIntent.kind === "token" && recoveryIntent.returnTo) {
      window.setTimeout(() => navigate(recoveryIntent.returnTo as string, { replace: true }), 650);
    }
  }

  async function handleRecoveryConfirmation() {
    if (busy || recoveryIntent.kind !== "token") return;
    setBusy(true);
    setError(null);
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: recoveryIntent.tokenHash,
      type: "recovery",
    });
    setBusy(false);
    if (verifyError || !data.session) {
      setConfirmState("invalid");
      setError("This password link is invalid or has expired. Request a new link to continue.");
      return;
    }
    setConfirmState("ready");
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="bdse-glass-card w-[420px] max-w-[92vw] px-10 pt-8 pb-7 text-center">
        <div className="-mt-1 -mb-2">
          <img
            src={bdseLogoUrl}
            alt="BD Signal Engine"
            className="mx-auto w-60 h-auto"
            style={{ mixBlendMode: "multiply" }}
          />
        </div>

        <p className="bdse-tagline mb-4">
          {mode === "request" ? "Reset your password" : "Set a new password"}
        </p>

        {error && (
          <div className="bdse-error-banner mb-3" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        {success && (
          <div
            className="text-xs text-bdse-green px-2.5 py-1.5 rounded-[10px] mb-3"
            style={{
              background: "rgba(58,158,126,0.08)",
              border: "1px solid rgba(58,158,126,0.2)",
            }}
            role="status"
          >
            {success}
          </div>
        )}

        {mode === "request" ? (
          <form onSubmit={handleRequest} className="space-y-3 text-left">
            <div>
              <label htmlFor="email" className="bdse-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bdse-input"
                disabled={busy}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !email}
              className="bdse-btn-primary w-full mt-1"
            >
              {busy ? "Sending..." : "Send reset link"}
            </button>
          </form>
        ) : confirmState === "checking" ? (
          <p className="text-sm text-bdse-text-secondary" role="status">
            Checking your secure password link…
          </p>
        ) : confirmState === "awaiting_confirmation" ? (
          <div className="space-y-3 text-left">
            <p className="text-sm text-bdse-text-secondary">
              To protect your one-time link from automated email scanners, confirm that you want to
              continue before BDSE opens the password form.
            </p>
            <button
              type="button"
              disabled={busy}
              className="bdse-btn-primary w-full"
              onClick={() => void handleRecoveryConfirmation()}
            >
              {busy ? "Checking link…" : "Continue securely"}
            </button>
          </div>
        ) : confirmState === "invalid" ? (
          <div className="space-y-3 text-left">
            <Link to="/forgot-password" className="bdse-btn-primary block w-full text-center">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-3 text-left">
            <div>
              <label htmlFor="new-password" className="bdse-label">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="bdse-input"
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="bdse-label">
                Confirm password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                className="bdse-input"
                disabled={busy}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !newPassword || !confirmPassword}
              className="bdse-btn-primary w-full mt-1"
            >
              {busy ? "Updating..." : "Update password"}
            </button>
          </form>
        )}

        <div className="mt-4 flex justify-center">
          <Link
            to="/login"
            className="text-xs text-bdse-text-tertiary hover:text-bdse-teal transition-colors duration-150"
          >
            Back to sign in
          </Link>
        </div>

        <div className="mt-4 pt-3.5 border-t border-black/[0.06]">
          <p className="bdse-footnote">Ankura BD Intelligence Platform</p>
        </div>
      </div>
    </div>
  );
}
